/**
 * Quick Filters Synchronization
 * Keeps quick filters and sidebar filters in sync
 */

class QuickFiltersSync {
  constructor() {
    this.isUpdating = false; // Flag to prevent infinite loops
  }

  init() {
    // Update quick filters state on initial load
    this.updateQuickFiltersFromCurrentUrl();

    // Listen for theme loading events to update quick filters state
    document.addEventListener('theme:loading:end', (event) => {
      this.updateQuickFiltersFromCurrentUrl();
    });

    // Listen for quick filter clicks
    document.addEventListener('click', (event) => {
      // Check if clicked element is a quick filter link or its child
      const quickFilterLink = event.target.closest('.quick-filter-container');

      if (quickFilterLink && !this.isUpdating) {
        event.preventDefault(); // Prevent default navigation

        // Check if this is the "All Products" card
        const isAllProducts = quickFilterLink.getAttribute('data-is-all-products') === 'true';

        if (isAllProducts) {
          // For "All Products", just navigate to the clean collection URL
          const cleanUrl = new URL(quickFilterLink.getAttribute('href'), window.location.origin);

          // Add section_id for AJAX update
          const sectionId =
            document.querySelector('facets-form')?.getAttribute('section-id') ||
            document.querySelector('.shopify-section')?.id?.replace('shopify-section-', '');
          if (sectionId) {
            cleanUrl.searchParams.set('section_id', sectionId);
          }

          // Trigger facet update event
          document.documentElement.dispatchEvent(
            new CustomEvent('facet:update', {
              bubbles: true,
              detail: {
                url: cleanUrl,
              },
            }),
          );
        } else {
          // Regular filter logic
          const filterValue = quickFilterLink.getAttribute('data-value');
          const filterParamName = quickFilterLink.getAttribute('data-filter-param');

          if (filterValue && filterParamName) {
            // Build URL with multiple filter support
            const newUrl = this.buildUrlWithFilter(filterValue, filterParamName);

            // Trigger facet update event
            document.documentElement.dispatchEvent(
              new CustomEvent('facet:update', {
                bubbles: true,
                detail: {
                  url: newUrl,
                },
              }),
            );
          }
        }
      }
    });

    // Listen for sidebar filter changes to sync with quick filters
    document.addEventListener('change', (event) => {
      if (
        event.target.name &&
        event.target.name.startsWith('filter.') &&
        !event.target.classList.contains('quick-filter-checkbox') &&
        !this.isUpdating
      ) {
        this.syncSidebarToQuickFilter(event.target);
      }
    });
  }

  /**
   * Update quick filters visual state based on current browser URL
   */
  updateQuickFiltersFromCurrentUrl() {
    const currentUrl = new URL(window.location.href);
    const urlParams = new URLSearchParams(currentUrl.search);

    // Check if any filter parameters are active
    const hasActiveFilters = Array.from(urlParams.keys()).some((key) => key.startsWith('filter.'));

    // Get all quick filter cards
    const quickFilterCards = document.querySelectorAll('.quick-filter__card');

    quickFilterCards.forEach((card) => {
      const container = card.closest('.quick-filter-container');
      if (!container) return;

      const isAllProducts = container.getAttribute('data-is-all-products') === 'true';

      if (isAllProducts) {
        // "All Products" card is active only when NO filters are active
        if (hasActiveFilters) {
          card.classList.remove('active');
        } else {
          card.classList.add('active');
        }
      } else {
        // Regular filter logic
        const filterValue = container.getAttribute('data-value');
        if (!filterValue) return;

        // Check if this filter value is present in the URL parameters
        let isActive = false;
        for (const [key, value] of urlParams) {
          if (key.startsWith('filter.') && value === filterValue) {
            isActive = true;
            break;
          }
        }

        // Update visual state
        if (isActive) {
          card.classList.add('active');
        } else {
          card.classList.remove('active');
        }
      }
    });
  }

  /**
   * Update quick filters visual state based on URL parameters
   */
  updateQuickFiltersFromUrl(url) {
    const urlParams = new URLSearchParams(url.search);

    // Check if any filter parameters are active
    const hasActiveFilters = Array.from(urlParams.keys()).some((key) => key.startsWith('filter.'));

    // Get all quick filter cards
    const quickFilterCards = document.querySelectorAll('.quick-filter__card');

    quickFilterCards.forEach((card) => {
      const container = card.closest('.quick-filter-container');
      if (!container) return;

      const isAllProducts = container.getAttribute('data-is-all-products') === 'true';

      if (isAllProducts) {
        // "All Products" card is active only when NO filters are active
        if (hasActiveFilters) {
          card.classList.remove('active');
        } else {
          card.classList.add('active');
        }
      } else {
        // Regular filter logic
        const filterValue = container.getAttribute('data-value');
        if (!filterValue) return;

        // Check if this filter value is present in the URL parameters
        let isActive = false;
        for (const [key, value] of urlParams) {
          if (key.startsWith('filter.') && value === filterValue) {
            isActive = true;
            break;
          }
        }

        // Update visual state
        if (isActive) {
          card.classList.add('active');
        } else {
          card.classList.remove('active');
        }
      }
    });
  }

  /**
   * Sync quick filter change to sidebar filter
   */
  syncQuickFilterToSidebar(quickFilterCheckbox) {
    this.isUpdating = true;

    const filterName = quickFilterCheckbox.dataset.filterName;
    const filterValue = quickFilterCheckbox.dataset.filterValue;

    // Find corresponding sidebar filter (they use dot-checkbox class and are NOT quick-filter-checkbox)
    const sidebarFilter = document.querySelector(
      `input.dot-checkbox[name="${filterName}"][value="${filterValue}"]:not(.quick-filter-checkbox)`,
    );

    if (sidebarFilter) {
      // Find the label associated with this checkbox using closest parent approach
      const checkboxControl = sidebarFilter.closest('.checkbox-control');
      const sidebarFilterLabel = checkboxControl ? checkboxControl.querySelector('label') : null;

      if (sidebarFilterLabel) {
        // Click on the label (this is usually what triggers the checkbox in custom designs)

        sidebarFilterLabel.click();
      } else if (checkboxControl) {
        // If no label, try clicking on the checkbox control wrapper

        checkboxControl.click();
      } else {
        // Fallback to clicking the checkbox directly

        sidebarFilter.click();
      }
    } else {
    }

    this.isUpdating = false;
  } /**
   * Sync sidebar filter change to quick filter
   */
  syncSidebarToQuickFilter(sidebarFilterCheckbox) {
    this.isUpdating = true;

    const filterValue = sidebarFilterCheckbox.value;
    const isChecked = sidebarFilterCheckbox.checked;

    // Find corresponding quick filter card using data-value
    const quickFilterCard = document.querySelector(`.quick-filter__card[data-value="${filterValue}"]`);

    if (quickFilterCard) {
      // Update visual state
      if (isChecked) {
        quickFilterCard.classList.add('active');
      } else {
        quickFilterCard.classList.remove('active');
      }

      // Update checkbox state
      const quickFilterCheckbox = document.querySelector(`.quick-filter-checkbox[data-filter-value="${filterValue}"]`);
      if (quickFilterCheckbox) {
        quickFilterCheckbox.checked = isChecked;
      }
    }

    this.isUpdating = false;
  }

  /**
   * Build URL with filter logic - add if not present, remove if present
   */
  buildUrlWithFilter(filterValue, filterParamName) {
    const currentUrl = new URL(window.location.href);

    if (!filterParamName) {
      // Fallback to the old method if param name is not provided
      filterParamName = this.getFilterParamName(filterValue);
      if (!filterParamName) {
        console.warn('Could not find filter parameter name for:', filterValue);
        return currentUrl;
      }
    }

    // Check if filter is currently active
    const currentFilterValues = currentUrl.searchParams.getAll(filterParamName);
    const isFilterActive = currentFilterValues.includes(filterValue);

    if (isFilterActive) {
      // Remove this filter value
      currentUrl.searchParams.delete(filterParamName);
      // Re-add all other values except the one we're removing
      currentFilterValues.forEach((value) => {
        if (value !== filterValue) {
          currentUrl.searchParams.append(filterParamName, value);
        }
      });
    } else {
      // Add this filter value
      currentUrl.searchParams.append(filterParamName, filterValue);
    }

    // Ensure section_id is set for AJAX updates
    const sectionId =
      document.querySelector('facets-form')?.getAttribute('section-id') ||
      document.querySelector('.shopify-section')?.id?.replace('shopify-section-', '');
    if (sectionId) {
      currentUrl.searchParams.set('section_id', sectionId);
    }

    // Remove page parameter when filters change
    currentUrl.searchParams.delete('page');

    return currentUrl;
  }

  /**
   * Get the filter parameter name for a given filter value
   */
  getFilterParamName(filterValue) {
    // We need to find which filter this value belongs to
    // Look through all form inputs to find the matching one
    const filterInputs = document.querySelectorAll('facets-form input[name^="filter."]');

    for (const input of filterInputs) {
      if (input.value === filterValue) {
        return input.name;
      }
    }

    // Fallback: try to find from quick filter sections settings
    const quickFilterSection = document.querySelector('.shopify-section--quick-filters');
    if (quickFilterSection) {
      // This would need to be extracted from the section settings
      // For now, assume it's a common filter name pattern
      return 'filter.v.option.category'; // This should be dynamic based on your filter setup
    }

    return null;
  }
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const quickFiltersSync = new QuickFiltersSync();
    quickFiltersSync.init();
  });
} else {
  const quickFiltersSync = new QuickFiltersSync();
  quickFiltersSync.init();
}
